import { createObservables } from '@client/helpers/observable.helpers';
import { getStoreyById } from '@shared/helpers/design/storey.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { DraftShape, Shape, Storey } from '@shared/models/design/storey.model';
import { Coordinate } from '@shared/models/geography.model';
import { Point2D } from '@shared/models/geometry.model';
import { MapEventOf, MapEventType, MapMouseEvent } from 'mapbox-gl';
import { Observable, Subject, fromEventPattern, takeUntil } from 'rxjs';
import { convertCoordinateToMetricOffset } from '@shared/helpers/geometry/coordinates.helpers';
import { point2dsToCoordinates } from '@shared/helpers/design/shape.helpers';
import { MapLayerIds } from './map-config.constants';
import {
  MapStoreyPointFeature,
  MapStoreyProperty,
} from '../models/map-feature.models';
import {
  projectLngLatAtElevation,
  screenToLngLatAtElevation,
} from './map.helpers';
import { mapGuards } from './map-guards.helpers';
import {
  DraftClickPayload,
  LineClickPayload,
  MapSelectionObservables,
  MapSelections,
  PointMeta,
} from '../models/map-observables.models';
import { toPoint2D } from '@shared/helpers/geometry/geometry.helpers';
import { isInputEvent } from '@shared/helpers/event.helpers';
import { getDraftPointFeatureFromMapEvent } from './map-features.helpers';
import { mapRenderingConstants } from '../map.constants';

export type MapEventName =
  | 'click'
  | 'dblclick'
  | 'mousedown'
  | 'mouseup'
  | 'mousemove'
  | 'touchstart'
  | 'touchmove'
  | 'touchend';

export type LayerEventName = MapEventName | 'mouseenter' | 'mouseleave';

export type MapObservables = {
  /**
   * DOM events (original events happening on the canvas)
   */
  dom: ReturnType<typeof createObservables>;

  /**
   * Map events (events happening on the map)
   */
  map: <T extends MapEventType>(type: T) => Observable<MapEventOf<T>>;

  /**
   * Layer events (events happening on a specific layer in the map)
   */
  layer: <T extends MapEventType>(
    type: T,
    layerId: string,
  ) => Observable<MapEventOf<T>>;

  /**
   * Destroy the observables and unsubscribe from all events
   */
  destroy: () => void;
};

/**
 * Get a destroy subject from a map
 * @param map
 * @returns
 */
const getDestroySubjectFromMap = (map: mapboxgl.Map): Subject<void> => {
  const destroy$ = new Subject<void>();
  const emit = (): void => {
    if (!destroy$.closed) {
      destroy$.next();
      destroy$.complete();
    }
  };
  map.once('remove', emit);
  destroy$.subscribe(() => {
    map.off('remove', emit);
  });
  return destroy$;
};

/**
 * Create observables for a map
 * @param map The map to create observables for
 * @param $destroy Provide a destroy subject to unsubscribe from events when the subject is nexted. Will default to a new subject if not provided (which will never be destroyed)
 * @returns An object with the observables for the map
 */

/**
 * Prevent DOM event emissions when typing inside inputs and text areas
 * @param event Native event
 */
const allowMapEvent = (event: MouseEvent | KeyboardEvent): boolean =>
  !isInputEvent(event);

export const createMapObservables = (map: mapboxgl.Map): MapObservables => {
  const destroy$ = getDestroySubjectFromMap(map);
  const dom = createObservables(map.getCanvas(), destroy$, allowMapEvent);

  const mapEvent = <T extends MapEventType>(
    type: T,
  ): Observable<MapEventOf<T>> =>
    fromEventPattern<MapEventOf<T>>(
      (handler) => map.on(type as any, handler as any),
      (handler) => map.off(type as any, handler as any),
    ).pipe(takeUntil(destroy$)); // Automatically unsubscribe from events when the destroy subject is nexted

  const layerEvent = <T extends MapEventType>(
    type: T,
    layerId: string,
  ): Observable<MapEventOf<T>> =>
    fromEventPattern<MapEventOf<T>>(
      (handler) => map.on(type as any, layerId, handler as any),
      (handler) => map.off(type as any, layerId, handler as any),
    ).pipe(takeUntil(destroy$)); // Automatically unsubscribe from events when the destroy subject is nexted

  const destroy = () => {
    destroy$.next();
    destroy$.complete();
  };

  return {
    dom,
    map: mapEvent,
    layer: layerEvent,
    destroy,
  };
};

// TODO FIX THIS
export const isDraftClickPayload = (
  payload: DraftClickPayload | undefined,
): payload is DraftClickPayload => guards.is.defined(payload);

// TODO FIX THIS
export const isLineClickPayload = (
  payload: LineClickPayload | undefined,
): payload is LineClickPayload => guards.is.defined(payload);

export const wrapPreventDefault = (event: MapMouseEvent): (() => void) => {
  return () => {
    if (typeof event.preventDefault === 'function') event.preventDefault();
  };
};

export const getEventPosition = (
  event: MapMouseEvent,
  mapInstance: mapboxgl.Map | undefined,
  elevation: number | undefined,
  origin: Coordinate,
): Point2D => {
  const shouldProjectAtElevation =
    !!mapInstance && typeof elevation === 'number' && elevation !== 0;
  const coordinate = shouldProjectAtElevation
    ? screenToLngLatAtElevation(
        mapInstance,
        event.point ?? mapInstance.project(event.lngLat),
        elevation,
      )
    : event.lngLat;
  const [mx, my] = convertCoordinateToMetricOffset(
    { lat: coordinate.lat, lng: coordinate.lng },
    origin,
  );
  return [mx, -my] as Point2D;
};

const isAltModifierActive = (event: MapMouseEvent | MouseEvent): boolean => {
  const originalEvent =
    event instanceof MouseEvent
      ? event
      : (event?.originalEvent as
          MouseEvent | KeyboardEvent | TouchEvent | PointerEvent | undefined);
  if (
    originalEvent &&
    'altKey' in originalEvent &&
    typeof (originalEvent as { altKey?: boolean }).altKey === 'boolean'
  ) {
    return Boolean((originalEvent as { altKey?: boolean }).altKey ?? false);
  }
  if (typeof window !== 'undefined') {
    return Boolean((window.event as KeyboardEvent | undefined)?.altKey);
  }
  return false;
};

export const queryPointFeature = (
  map: mapboxgl.Map,
  event: MapMouseEvent,
): MapStoreyPointFeature | undefined => {
  if (!map.getLayer(MapLayerIds.Points)) return undefined;
  const feature = map.queryRenderedFeatures(event.point, {
    layers: [MapLayerIds.Points],
  })?.[0];
  return mapGuards.is.feature.point(feature) ? feature : undefined;
};

export const getSelectedStoreyShape = (
  storeys: Storey[],
  selectedStoreyId: Storey['id'] | undefined,
  shapeId: string | undefined,
): Shape | undefined => {
  if (!shapeId) return undefined;
  const storey = getStoreyById(storeys, selectedStoreyId);
  if (!storey) return undefined;
  return storey.shapes.find((shape) => shape.id === shapeId);
};

export const resolvePointMeta = (
  feature: MapStoreyPointFeature | undefined,
  draftShape: DraftShape | undefined,
  storeys: Storey[],
  selectedStoreyId: Storey['id'] | undefined,
): PointMeta | undefined => {
  if (!feature) return undefined;
  const properties = feature.properties ?? {}; // Can be removed later

  const shape = properties.draft
    ? draftShape
    : getSelectedStoreyShape(storeys, selectedStoreyId, properties.shapeId);
  const originPoint = shape?.points?.[properties.index];
  if (!originPoint) return undefined;

  return {
    shape,
    shapeId: shape.id,
    storeyId: selectedStoreyId,
    isDraft: properties.draft ?? false,
    index: properties.index,
    origin: toPoint2D(originPoint),
    elevation: properties[MapStoreyProperty.Elevation] ?? 0,
  };
};

/**
 * Screen-pixel radius for closing a draft by clicking near the first vertex.
 * Pen vertices are {@link mapRenderingConstants.penPointSizePx}; Alt-draw keeps
 * the larger pre-pen target so closing is not harder after the pen tool landed.
 */
export const getDraftCloseDistancePx = (penMode: boolean): number =>
  penMode
    ? mapRenderingConstants.penPointSizePx / 2
    : mapRenderingConstants.altDrawCloseDistancePx;

type DraftClickPayloadOptions = {
  allowWithoutAlt?: boolean;
  treatAsAlt?: boolean;
  penMode?: boolean;
};

/**
 * Build a payload for draft shape click interactions.
 * @param map {@link mapboxgl.Map} instance
 * @param event {@link MapMouseEvent} click event
 * @param draftShape Current {@link DraftShape} or undefined
 * @param elevation Storey elevation in metres
 * @param origin {@link Coordinate} used for metric conversion
 * @param options.allowWithoutAlt Allow starting a draft without the Alt modifier
 * @param options.treatAsAlt Treat the click as Alt for cutout / new-solid intent
 * @param options.penMode When true, close-distance matches the 15px pen vertex
 */
export const buildDraftClickPayload = (
  map: mapboxgl.Map,
  event: MapMouseEvent,
  draftShape: DraftShape | undefined,
  elevation = 0,
  origin: Coordinate,
  options: DraftClickPayloadOptions = {},
): DraftClickPayload | undefined => {
  const allowWithoutAlt = options.allowWithoutAlt ?? false;
  const penMode = options.penMode ?? false;
  // Draw-with-building mirrors holding Alt for first-point cutout vs solid routing.
  const altActive = isAltModifierActive(event) || Boolean(options.treatAsAlt);
  const hasDraft = Boolean(draftShape);

  const draftPointFeature = getDraftPointFeatureFromMapEvent(event);

  // Close draft shape if clicked on first point and shape has at least 3 points
  if (draftPointFeature && draftShape) {
    const pointIndex = draftPointFeature.properties.index;
    const isFirst = pointIndex === 0;
    const isLastPoint = pointIndex === draftShape.points.length - 1;
    if (isFirst && draftShape.points.length >= 3) {
      return {
        type: 'complete',
        preventDefault: wrapPreventDefault(event),
        reason: 'first-point',
      };
    }
    // Click on last point (add-point): add a new point at click location
    if (isLastPoint) {
      const position = getEventPosition(event, map, elevation, origin);
      return {
        type: 'add',
        preventDefault: wrapPreventDefault(event),
        position,
      };
    }
    return undefined;
  }

  if (!altActive && !hasDraft && !allowWithoutAlt) return undefined;

  if (draftShape && draftShape.points.length >= 3 && draftShape.points[0]) {
    const allCoords = point2dsToCoordinates(origin, draftShape);
    const firstPointCoords = allCoords[0];
    if (firstPointCoords) {
      const firstPointScreen = projectLngLatAtElevation(
        map,
        firstPointCoords,
        elevation,
      );
      const clickScreen = toPoint2D(event.point ?? map.project(event.lngLat));
      const dx = clickScreen[0] - firstPointScreen[0];
      const dy = clickScreen[1] - firstPointScreen[1];
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= getDraftCloseDistancePx(penMode)) {
        return {
          type: 'complete',
          preventDefault: wrapPreventDefault(event),
          reason: 'close-distance',
        };
      }
    }
  }

  const position = getEventPosition(event, map, elevation, origin);
  return {
    type: 'add',
    preventDefault: wrapPreventDefault(event),
    position,
    cutoutMode: draftShape?.cutout === true || altActive,
  };
};

/**
 * Sibling footprint clicks select that building and must not also extend a
 * draft. Pen mode still places vertices on drawn siblings; empty-building
 * hit targets stay selectable so a demolished slot can be targeted.
 */
export const shouldIgnoreDraftClickOnOtherBuilding = (options: {
  penMode: boolean;
  otherBuildingId: string | undefined;
  isEmptyOtherBuilding?: boolean;
}): boolean => {
  if (!options.otherBuildingId) return false;
  if (!options.penMode) return true;
  return Boolean(options.isEmptyOtherBuilding);
};

/**
 * Whether a sibling-building click should change the selected building.
 * Vertices and walls of the selected building still win. Overlapping volumes
 * switch when the sibling's surface at the pointer is nearer the camera.
 */
export const shouldSwitchToOtherBuilding = (options: {
  otherBuildingId: string | undefined;
  selectedBuildingHit: boolean;
  /** Sibling surface is nearer the camera than the selected building. */
  otherBuildingIsHigher?: boolean;
}): boolean => {
  if (!options.otherBuildingId) return false;
  if (!options.selectedBuildingHit) return true;
  return options.otherBuildingIsHigher === true;
};

/**
 * Whether a map background click should clear the building selection
 * (DEV-2576). Drafting, pen mode and an empty selected building all use
 * background clicks to draw, so the selection is kept there.
 */
export const shouldClearBuildingSelection = (options: {
  hasBuildingSelection: boolean;
  hasDraftShape: boolean;
  penMode: boolean;
  selectedBuildingIsEmpty: boolean;
}): boolean => {
  if (!options.hasBuildingSelection) return false;
  if (options.hasDraftShape) return false;
  if (options.penMode) return false;
  return !options.selectedBuildingIsEmpty;
};

/**
 * Whether selecting another building should drop the in-progress draft.
 * Pen mode keeps the draft so an empty-slot click can still close into that
 * building; Alt-draw and other non-pen drafts still reset on select.
 */
export const shouldClearDraftOnBuildingSelect = (penMode: boolean): boolean =>
  !penMode;

/** Second Escape leaves Draw only after the project already has geometry. */
export const shouldExitDrawToolOnEscape = (input: {
  drawToolActive: boolean;
  hasDraft: boolean;
  hasDrawnStorey: boolean;
}): boolean => input.drawToolActive && !input.hasDraft && input.hasDrawnStorey;

type ShouldApplyDraftAfterAwaitInput = {
  isDestroyed: boolean;
  startedEpoch: number;
  currentEpoch: number;
  /** Pen writes must still be in pen mode; omit for Alt-draw. */
  penMode?: boolean;
};

/**
 * Whether a draft vertex write that awaited persist should still apply.
 * Esc, leaving pen, and draft discard bump
 * {@link ShouldApplyDraftAfterAwaitInput.currentEpoch}; destroy or leaving pen
 * mode drops the write so a discarded draft cannot return.
 */
export const shouldApplyDraftAfterAwait = (
  input: ShouldApplyDraftAfterAwaitInput,
): boolean => {
  if (input.isDestroyed) return false;
  if (input.startedEpoch !== input.currentEpoch) return false;
  if (input.penMode === false) return false;
  return true;
};

/**
 * Get the current selections from the observables
 * @param observables The observables to get the selections from
 * @returns The current selections
 */
export const getMapSelectionsFromObservables = (
  observables: MapSelectionObservables,
): MapSelections => {
  return {
    storeyId: observables.storeyId$.value,
    pointIndexes: observables.pointIndexes$.value,
    shapeId: observables.shapeId$.value,
    draftShape: observables.draftShape$.value,
  };
};
